import { useEffect, useState } from "react";
import { fileApi, FileObject } from "shared/api/fileApi";
import { logger } from "shared/utils/logger";

/**
 * Constructs a file uploader which accepts many {@link File} objects and yields many {@link FileObject} documents
 */
export function useUploadQueue(opts: {
    usage?: "general" | "ipaws";
    /**
     * Fired in chunks with the new {@link FileObject} documents after the upload queue is cleared
     */
    onUploaded: (documents: FileObject[]) => unknown;
}) {
    const [queue, setQueue] = useState<File[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [buffer, setBuffer] = useState<FileObject[]>([]);

    useEffect(() => {
        if (queue.length == 0) {
            // flush buffer
            if (buffer.length > 0) opts.onUploaded(buffer);
            setBuffer([]);

            setIsLoading(false);
            return;
        }

        const [next, ...tail] = queue;
        setIsLoading(true);

        fileApi
            .upload(next, { usage: opts.usage })
            .then((doc) => setBuffer((buf) => buf.concat([doc])))
            .then(() => setQueue(tail))
            .catch(logger.error);
    }, [queue]);

    return {
        /**
         * Pushes a file to the upload queue
         */
        enqueue(file: File) {
            setQueue((q) => q.concat([file]));
        },
        /**
         * Whether or not there is currently an upload processing
         */
        isLoading,
    };
}
